import { withModel } from '@rexlabs/model-generator';
import { autobind } from 'core-decorators';
import { debounce } from 'lodash';
import React, { PureComponent } from 'react';
import session from 'src/data/models/custom/session';
import { getAddressDetails } from 'src/utils/address';
import { api } from 'src/utils/api-client';
import getFakeEvent from 'src/utils/fake-event';
import { getSearchCountries } from 'src/utils/location-countries';
import Select from './default';

@withModel(session)
@autobind
class AddressSelect extends PureComponent {
  static defaultProps = {
    pluckValue: (datum) => {
      // NOTE: hacky workaround, Select call pluckValue to determine
      // the `isSelected` state, but it sometimes gets called with the
      // option data and sometimes with its value, so we need to return
      // either the option.id or the option.value, depending on context...
      return datum ? datum.id || datum.value : null;
    },
    pluckLabel: (datum) => (datum = datum ? datum.label : null),
    placeholder: 'Select address...'
  };

  state = {
    search: '',
    suggestions: [],
    loading: false
  };

  debouceGetSuggestions = debounce(this.getSuggestions, 400);

  getCountries() {
    const { givenCountries, session } = this.props;
    return getSearchCountries({ givenCountries, session });
  }

  getSuggestions(searchValue) {
    if (!searchValue) {
      this.setState({ loading: false, suggestions: [] });
      return;
    }

    const countries = this.getCountries();

    this.setState({ loading: true }, () => {
      api
        .get(
          `/location/autocomplete/predict?type=address&countries=${countries}&input=${searchValue}`
        )
        .then((result) => {
          const suggestions = (result?.data ?? []).map((locData) => ({
            value: locData.id,
            label: locData.label,
            data: locData
          }));
          this.setState({
            loading: false,
            suggestions
          });
        })
        .catch(() => {
          this.setState({
            loading: false,
            suggestions: []
          });
        });
    });
  }

  filterSuggestions({ target: { value } }) {
    this.setState({ search: value, loading: true });
    this.debouceGetSuggestions(value);
  }

  async handleSelect(selected) {
    const { onChange, name } = this.props;
    let addressDetails = null;

    if (
      selected &&
      selected.value === (this.props.value && this.props.value.value)
    ) {
      return;
    }

    if (selected) {
      this.setState({ loading: true });
      addressDetails = await getAddressDetails(selected.value, selected.label);
    }

    this.setState({ search: '', suggestions: [], loading: false }, () => {
      const fakeEvent = getFakeEvent('select', name, name, addressDetails);
      if (onChange) {
        onChange(fakeEvent);
      }
      // We force the blur the input on select, cause otherwise
      // it will keep showing the search term until the user blurs
      // manually
      setTimeout(() => {
        if (addressDetails) {
          window.document.activeElement.blur();
        }
      }, 0);
    });
  }

  handleBlur(e) {
    const { onBlur } = this.props;
    this.setState({ search: '', suggestions: [] });
    if (onBlur) {
      onBlur(e);
    }
  }

  handleCleared() {
    const { name, onChange, onBlur } = this.props;
    this.setState({ search: '', suggestions: [] });
    const fakeEvent = getFakeEvent('select', name, name, undefined);
    if (onChange) {
      onChange(fakeEvent);
    }
    setTimeout(() => {
      if (onBlur) {
        onBlur(fakeEvent);
      }
    }, 0);
  }

  render() {
    const { value, onChange, onBlur, ...props } = this.props;
    const { suggestions, loading } = this.state;

    const options = suggestions.filter((o) => o.value);
    if (value?.value && !options.find((o) => o.value === value.value)) {
      options.push(value);
    }

    return (
      <Select
        isLoading={loading}
        options={options}
        onFieldChange={this.filterSuggestions}
        filter={(s) => s}
        DropdownIndicator={() => {
          // we want to hide the indicator
          return <span />;
        }}
        isClearable={true}
        value={value ? value.value : null}
        onSelect={this.handleSelect}
        onBlur={this.handleBlur}
        onCleared={this.handleCleared}
        {...props}
      />
    );
  }
}

export default AddressSelect;
